import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation,
  forwardRef,
  EventEmitter,
  Input,
  Output,
  SkipSelf,
  Optional,
  TemplateRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import mixinDisabled from '../../mixins/disabled';
import { PhxContextService, PHOENIX_CONTEXTS } from '../../services/phx-context-service';
import { POSITION_TYPE } from '../dropdown/overlay-position';
import { PhxToggleSize } from './phx-toggle.types';

const baseToggleMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: 'phx-toggle',
  inputs: ['disabled'],
  host: {
    class: 'phx-toggle-component',
    '[class.phx-toggle-lg]': `phxSize === 'large'`,
    '[class.phx-toggle-md]': `phxSize === 'medium'`,
    '[class.phx-toggle-sm]': `phxSize === 'small'`,
    '[class.phx-filter]': 'phxStyleType === PHOENIX_CONTEXTS.FILTER',
    '[class.phx-toggle-disabled]': 'disabled',
    '[class.phx-toggle-full-width]': 'phxToggleFullWidth',
    '[attr.phx-non-default-selected]': 'isNonDefaultSelected',
    '[class.phx-toggle-error]': 'showError',
    '(keydown)': 'onKeydown($event)',
  },
  template: `
    <ng-container *ngIf="!phxSwitch">
      <button
        class="phx-toggle"
        [ngClass]="{
          'phx-on': phxToggle,
          'phx-off': !phxToggle,
          'phx-toggle-icon-only': phxToggleIconOnly
        }"
        [attr.disabled]="disabled || null"
        phx-tooltip
        [phxContent]="phxTooltipContent"
        [phxPlacement]="phxTooltipPlacement"
        (click)="onToggle()"
        (blur)="onTouched()"
      >
        <ng-content></ng-content>
      </button>
    </ng-container>
    <ng-container *ngIf="phxSwitch">
      <div class="phx-switch" [attr.tabindex]="disabled ? null : 0" (keyup.enter)="onToggle()" (blur)="onTouched()">
        <label [ngClass]="phxToggle ? 'phx-on' : 'phx-off'" [attr.disabled]="disabled || null">
          <input type="checkbox" value="" [checked]="phxToggle" [disabled]='disabled' (change)="onToggle()" />
          <span class="phx-switch-thumb"></span>
        </label>
      </div>
    </ng-container>
  `,
  styleUrls: ['./phx-toggle.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxToggleComponent),
      multi: true,
    },
  ],
})
export class PhxToggleComponent extends baseToggleMixin implements ControlValueAccessor {
  @Input() phxSwitch: boolean = false;
  @Input() phxSize: PhxToggleSize = 'medium';
  @Input() phxToggle: boolean = true;
  @Input() phxToggleIconOnly: boolean = false;
  @Input() phxTooltipContent: string | TemplateRef<any> | null;
  @Input() phxTooltipPlacement: POSITION_TYPE = 'top';
  @Input() phxToggleFullWidth: boolean = false;
  @Input() phxDefaultValue: boolean;
  @Input() phxHasError?: boolean;

  @Output() phxToggleChange = new EventEmitter<boolean>();
  public readonly PHOENIX_CONTEXTS = PHOENIX_CONTEXTS;
  public phxStyleType: PHOENIX_CONTEXTS;

  onChange(value: boolean) {}

  onTouched() {}

  onToggle() {
    if (!this.disabled) {
      this.phxToggle = !this.phxToggle;
      this.phxToggleChange.emit(this.phxToggle);
      this.onChange(this.phxToggle);
    }
  }

  constructor(private _cdr: ChangeDetectorRef, @Optional() @SkipSelf() contextService: PhxContextService) {
    super();
    this.phxStyleType = contextService?.getType();
  }

  writeValue(value: boolean): void {
    this.phxToggle = value;
    this._cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.onToggle();
    }
  }
  
  get isNonDefaultSelected(): boolean {
    if (!this.phxDefaultValue) return this.phxToggle;
    return this.phxToggle !== this.phxDefaultValue;
  }

  get showError(): boolean {
    return this.phxHasError;
  }

}
